import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { X, Save, User, Mail, Phone, MapPin, Shield, Eye, EyeOff } from 'lucide-react';
import { useUsers } from 'features/administrativo/hooks/useUsers';
import CompanyAutocomplete from '../../../components/common/CompanyAutocomplete';

const buildInitialFormData = (user) => ({
  full_name: user?.full_name || '',
  corporate_email: user?.corporate_email || '',
  company: user?.companies && user.companies.length > 0 ? user.companies[0] : null,
  phone: user?.phone || '',
  city: user?.city || '',
  password: '',
  access_level: user?.access_level || 'company_manager'
});

const normalizeFormData = (data) => ({
  full_name: String(data?.full_name || '').trim(),
  corporate_email: String(data?.corporate_email || '').trim(),
  company_id: data?.company && typeof data.company === 'object' ? data.company.id || null : null,
  company_name: data?.company && typeof data.company === 'object' ? String(data.company.name || '').trim() : String(data?.company || '').trim(),
  phone: String(data?.phone || '').trim(),
  city: String(data?.city || '').trim(),
  password: String(data?.password || ''),
  access_level: String(data?.access_level || 'company_manager')
});

const UserForm = ({ user, onClose, onUpdate }) => {
  const { createUser, updateUser } = useUsers();
  const isEditing = Boolean(user);

  const [formData, setFormData] = useState(() => buildInitialFormData(user));
  const [initialSnapshot, setInitialSnapshot] = useState(() => JSON.stringify(normalizeFormData(buildInitialFormData(user))));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const titleId = 'user-form-title';
  const descriptionId = 'user-form-description';
  const fieldIds = {
    full_name: 'user-form-full-name',
    corporate_email: 'user-form-corporate-email',
    company: 'user-form-company',
    phone: 'user-form-phone',
    city: 'user-form-city',
    access_level: 'user-form-access-level',
    password: 'user-form-password'
  };

  const errorIds = {
    full_name: 'user-form-full-name-error',
    corporate_email: 'user-form-corporate-email-error',
    company: 'user-form-company-error',
    phone: 'user-form-phone-error',
    city: 'user-form-city-error',
    access_level: 'user-form-access-level-error',
    password: 'user-form-password-error',
    general: 'user-form-general-error'
  };

  const passwordHelperId = 'user-form-password-helper';
  const firstInputRef = useRef(null);
  const previousFocusedElementRef = useRef(null);

  useEffect(() => {
    previousFocusedElementRef.current = document.activeElement;
    requestAnimationFrame(() => {
      firstInputRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    const initialData = buildInitialFormData(user);
    setFormData(initialData);
    setInitialSnapshot(JSON.stringify(normalizeFormData(initialData)));
    setErrors({});
    setShowPassword(false);
  }, [user]);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(normalizeFormData(formData)) !== initialSnapshot;
  }, [formData, initialSnapshot]);

  const restoreFocusAndClose = useCallback(() => {
    onClose?.();
    requestAnimationFrame(() => {
      const element = previousFocusedElementRef.current;
      if (element && typeof element.focus === 'function') {
        element.focus();
      }
    });
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (isSubmitting) return;

    if (hasUnsavedChanges) {
      const shouldClose = window.confirm('Existem alterações não salvas. Deseja fechar mesmo assim?');
      if (!shouldClose) return;
    }

    restoreFocusAndClose();
  }, [hasUnsavedChanges, isSubmitting, restoreFocusAndClose]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      requestClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [requestClose]);

  const applyPhoneMask = (value) => {
    const numbers = value.replace(/\D/g, '');

    if (numbers.length <= 2) {
      return numbers;
    }

    if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)})${numbers.slice(2)}`;
    }

    if (numbers.length <= 11) {
      return `(${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    }

    return `(${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const validateEmail = (value) => {
    if (!value?.trim()) return 'E-mail corporativo é obrigatório';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'E-mail inválido';
    if (value.length > 255) return 'E-mail muito longo (máximo 255 caracteres)';
    return '';
  };

  const validatePhone = (value) => {
    if (!value?.trim()) return '';
    const phoneNumbers = value.replace(/\D/g, '');
    if (phoneNumbers.length < 10 || phoneNumbers.length > 11) {
      return 'Telefone deve ter 10 ou 11 dígitos';
    }
    return '';
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'full_name':
        return value?.trim() ? '' : 'Nome completo é obrigatório';
      case 'corporate_email':
        return validateEmail(value);
      case 'company':
        return value && value.name ? '' : 'Empresa é obrigatória';
      case 'password':
        if (!isEditing && !value?.trim()) return 'Senha é obrigatória para novos usuários';
        if (value && value.length < 8) return 'Senha deve ter pelo menos 8 caracteres';
        return '';
      case 'phone':
        return validatePhone(value);
      default:
        return '';
    }
  };

  const setFieldValidationError = (fieldName, message) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (message) {
        next[fieldName] = message;
      } else {
        delete next[fieldName];
      }
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'phone' ? applyPhoneMask(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue
    }));

    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      delete next.general;
      return next;
    });
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    if (!['corporate_email', 'phone'].includes(name)) return;

    const message = validateField(name, value);
    setFieldValidationError(name, message);
  };

  const handleCompanyChange = (company) => {
    setFormData((prev) => ({ ...prev, company }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.company;
      delete next.general;
      return next;
    });
  };

  const validateForm = () => {
    const validationErrors = {};

    ['full_name', 'corporate_email', 'company', 'password', 'phone'].forEach((fieldName) => {
      const fieldValue = fieldName === 'company' ? formData.company : formData[fieldName];
      const message = validateField(fieldName, fieldValue);
      if (message) {
        validationErrors[fieldName] = message;
      }
    });

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const firstInvalidField = ['full_name', 'corporate_email', 'company', 'phone', 'password'].find((key) => validationErrors[key]);
      if (firstInvalidField) {
        const element = document.getElementById(fieldIds[firstInvalidField]);
        element?.focus();
      }
      return false;
    }

    return true;
  };

  const getDescribedBy = (...ids) => {
    const validIds = ids.filter(Boolean);
    return validIds.length > 0 ? validIds.join(' ') : undefined;
  };

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      requestClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const userData = { ...formData };

      if (userData.company && userData.company.name) {
        const companyId = userData.company.id;
        const companyName = String(userData.company.name).trim();

        userData.company = companyName;
        userData.company_id = companyId;
      }

      if (isEditing && !userData.password) {
        delete userData.password;
      }

      if (isEditing) {
        await updateUser(user.id, userData);
      } else {
        await createUser(userData);
      }

      if (onUpdate) {
        onUpdate();
      }

      restoreFocusAndClose();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      console.error('Detalhes do erro:', error.response?.data || error.message);

      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        const nextErrors = {};

        Object.keys(backendErrors).forEach((field) => {
          if (backendErrors[field] && backendErrors[field].length > 0) {
            nextErrors[field] = backendErrors[field][0];
          }
        });

        setErrors(nextErrors);
      } else if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Erro ao salvar usuário. Tente novamente.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col theme-surface border"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 id={titleId} className="text-lg font-semibold theme-text">
                {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
              </h3>
              <p id={descriptionId} className="text-sm theme-text-muted">
                {isEditing ? 'Atualize as informações do usuário' : 'Preencha os dados do novo usuário'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={isSubmitting}
            aria-label="Fechar modal de usuário"
            className="h-11 w-11 inline-flex items-center justify-center rounded-lg transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            style={{ background: 'var(--surface-muted)' }}
          >
            <X className="h-5 w-5 theme-text-muted" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form id="userForm" onSubmit={handleSubmit} className="p-6 space-y-6">
            {errors.general && (
              <div
                id={errorIds.general}
                role="alert"
                aria-live="polite"
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <p className="text-sm text-red-800">{errors.general}</p>
              </div>
            )}

            <section className="space-y-4" aria-labelledby="user-form-personal-section">
              <div className="pb-2 border-b" style={{ borderColor: 'var(--border)' }}>
                <h4 id="user-form-personal-section" className="text-sm font-semibold tracking-wide uppercase theme-text-muted">
                  Dados Pessoais
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label htmlFor={fieldIds.full_name} className="block text-sm font-medium theme-text mb-2">
                    Nome Completo *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-muted h-4 w-4" />
                    <input
                      ref={firstInputRef}
                      id={fieldIds.full_name}
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      autoComplete="name"
                      aria-invalid={Boolean(errors.full_name)}
                      aria-describedby={getDescribedBy(errors.full_name ? errorIds.full_name : undefined)}
                      className={`w-full h-11 pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface transition-colors ${
                        errors.full_name ? 'border-red-300 bg-red-50/20' : ''
                      } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                      style={{ borderColor: errors.full_name ? undefined : 'var(--border)' }}
                      placeholder="Digite o nome completo"
                      required
                    />
                  </div>
                  {errors.full_name && (
                    <p id={errorIds.full_name} role="alert" aria-live="polite" className="mt-1 text-sm text-red-600">
                      {errors.full_name}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor={fieldIds.corporate_email} className="block text-sm font-medium theme-text mb-2">
                    E-mail Corporativo *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-muted h-4 w-4" />
                    <input
                      id={fieldIds.corporate_email}
                      type="email"
                      name="corporate_email"
                      value={formData.corporate_email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      autoComplete="email"
                      inputMode="email"
                      maxLength={255}
                      aria-invalid={Boolean(errors.corporate_email)}
                      aria-describedby={getDescribedBy(errors.corporate_email ? errorIds.corporate_email : undefined)}
                      className={`w-full h-11 pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface transition-colors ${
                        errors.corporate_email ? 'border-red-300 bg-red-50/20' : ''
                      } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                      style={{ borderColor: errors.corporate_email ? undefined : 'var(--border)' }}
                      placeholder="usuario@empresa.com"
                      required
                    />
                  </div>
                  {errors.corporate_email && (
                    <p id={errorIds.corporate_email} role="alert" aria-live="polite" className="mt-1 text-sm text-red-600">
                      {errors.corporate_email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor={fieldIds.company} className="block text-sm font-medium theme-text mb-2">
                    Empresa *
                  </label>
                  <CompanyAutocomplete
                    value={formData.company}
                    onChange={handleCompanyChange}
                    placeholder="Selecione uma empresa"
                    error={errors.company}
                    disabled={isSubmitting}
                    inputId={fieldIds.company}
                    ariaInvalid={Boolean(errors.company)}
                    ariaDescribedBy={getDescribedBy(errors.company ? errorIds.company : undefined)}
                    showErrorMessage={false}
                  />
                  {errors.company && (
                    <p id={errorIds.company} role="alert" aria-live="polite" className="mt-1 text-sm text-red-600">
                      {errors.company}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor={fieldIds.phone} className="block text-sm font-medium theme-text mb-2">
                    Telefone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-muted h-4 w-4" />
                    <input
                      id={fieldIds.phone}
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      autoComplete="tel"
                      inputMode="numeric"
                      maxLength={15}
                      pattern="[0-9\s\(\)\-\+]*"
                      title="Digite apenas números - a máscara será aplicada automaticamente"
                      aria-invalid={Boolean(errors.phone)}
                      aria-describedby={getDescribedBy(errors.phone ? errorIds.phone : undefined)}
                      className={`w-full h-11 pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface transition-colors ${
                        errors.phone ? 'border-red-300 bg-red-50/20' : ''
                      } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                      style={{ borderColor: errors.phone ? undefined : 'var(--border)' }}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  {errors.phone && (
                    <p id={errorIds.phone} role="alert" aria-live="polite" className="mt-1 text-sm text-red-600">
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor={fieldIds.city} className="block text-sm font-medium theme-text mb-2">
                    Cidade
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-muted h-4 w-4" />
                    <input
                      id={fieldIds.city}
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      autoComplete="address-level2"
                      className={`w-full h-11 pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface transition-colors ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                      style={{ borderColor: 'var(--border)' }}
                      placeholder="Cidade"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4" aria-labelledby="user-form-access-section">
              <div className="pb-2 border-b" style={{ borderColor: 'var(--border)' }}>
                <h4 id="user-form-access-section" className="text-sm font-semibold tracking-wide uppercase theme-text-muted">
                  Acesso
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label htmlFor={fieldIds.access_level} className="block text-sm font-medium theme-text mb-2">
                    Escopo de Acesso
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-muted h-4 w-4" />
                    <select
                      id={fieldIds.access_level}
                      name="access_level"
                      value={formData.access_level}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={`w-full h-11 pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface transition-colors ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <option value="company_manager">Acesso por Empresa</option>
                      <option value="group_manager">Acesso por Grupo</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor={fieldIds.password} className="block text-sm font-medium theme-text mb-2">
                    Senha {!isEditing && '*'}
                  </label>
                  <div className="relative">
                    <input
                      id={fieldIds.password}
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      autoComplete={isEditing ? 'current-password' : 'new-password'}
                      aria-invalid={Boolean(errors.password)}
                      aria-describedby={getDescribedBy(passwordHelperId, errors.password ? errorIds.password : undefined)}
                      className={`w-full h-11 px-4 py-2 pr-11 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface transition-colors ${
                        errors.password ? 'border-red-300 bg-red-50/20' : ''
                      } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                      style={{ borderColor: errors.password ? undefined : 'var(--border)' }}
                      placeholder={isEditing ? 'Deixe em branco para manter a senha atual' : 'Mínimo 8 caracteres'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      disabled={isSubmitting}
                      className="absolute inset-y-0 right-0 h-11 w-11 inline-flex items-center justify-center theme-text-muted hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p id={passwordHelperId} className="mt-1 text-xs theme-text-muted">
                    {isEditing ? 'Opcional: deixe em branco para manter a senha atual.' : 'Mínimo de 8 caracteres.'}
                  </p>
                  {errors.password && (
                    <p id={errorIds.password} role="alert" aria-live="polite" className="mt-1 text-sm text-red-600">
                      {errors.password}
                    </p>
                  )}
                </div>
              </div>
            </section>
          </form>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t theme-surface-muted" style={{ borderColor: 'var(--border)' }}>
          <button
            type="button"
            onClick={requestClose}
            disabled={isSubmitting}
            className="h-11 px-4 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors theme-text focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="userForm"
            disabled={isSubmitting}
            className="h-11 flex items-center justify-center gap-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            {isSubmitting ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{isSubmitting ? 'Salvando usuário...' : isEditing ? 'Atualizar' : 'Criar'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserForm;
